import { useEffect, useRef } from 'react';
import { Logger } from '../utils/logger';

/**
 * useVersionCheck — détecte un bundle SPA périmé et notifie le caller.
 *
 * Contexte (V4-GEN-WS-EVENT-LOST-RECOVERY) : l'app est une SPA. Un onglet resté
 * ouvert (ou restauré par le navigateur) depuis un déploiement antérieur continue
 * d'exécuter l'ANCIEN JS en mémoire — il ne re-fetch jamais index.html (routing
 * client-side), donc le `no-cache` d'index.html ne le sauve pas. Un tel bundle
 * périmé peut parler un protocole obsolète (ex: ancien handshake WebSocket) et
 * échouer silencieusement. On compare l'id de build embarqué (`__BUILD_ID__`) au
 * `version.json` courant (servi en no-cache) et on prévient dès qu'ils divergent.
 *
 * Choix délibéré : on NE recharge PAS silencieusement (un reload forcé
 * détruirait le contenu non sauvegardé d'un éditeur de post ou une génération en
 * cours). On remonte l'info au caller qui propose un rechargement à l'utilisateur.
 */

// Injecté par Vite (define). En dev (`vite dev`), le define existe aussi ; on
// garde un fallback défensif au cas où le build serait servi sans injection.
const CURRENT_BUILD_ID: string =
  typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min

interface UseVersionCheckOptions {
  /** Ne sonde que si true (ex: utilisateur authentifié). */
  enabled?: boolean;
  /** Appelé UNE seule fois quand un nouveau build est détecté. */
  onUpdateAvailable: () => void;
}

export function useVersionCheck({ enabled = true, onUpdateAvailable }: UseVersionCheckOptions): void {
  const notifiedRef = useRef(false);
  // Garde la dernière callback sans re-souscrire l'effet (évite un re-setInterval).
  const cbRef = useRef(onUpdateAvailable);
  cbRef.current = onUpdateAvailable;

  useEffect(() => {
    // Pas de check en dev (build id 'dev' : pas de version.json déployé pertinent).
    if (!enabled || CURRENT_BUILD_ID === 'dev') return;

    let cancelled = false;

    const check = async () => {
      if (notifiedRef.current || cancelled) return;
      try {
        // Cache-bust + no-store : on veut la vérité serveur, jamais un cache.
        const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        const latest = data?.buildId;
        if (latest && latest !== CURRENT_BUILD_ID && !cancelled) {
          notifiedRef.current = true;
          Logger.info('[VersionCheck] Nouveau build détecté — bundle courant périmé', {
            current: CURRENT_BUILD_ID,
            latest,
          });
          cbRef.current();
        }
      } catch {
        // Réseau indisponible / JSON invalide : silencieux, on retentera au prochain tick.
      }
    };

    // À l'ouverture, à chaque retour au premier plan, et périodiquement.
    void check();
    const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);
}
